/**
 * Copyright 2024 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch} from 'redux';

import {fetchUsageStatistics, filterUsageByType} from 'services/analytics.service';
import websocket from 'services/websocket.connections';
import {getAdjustedTime} from 'utility/date';
import {IPortalError, transformToPortalError} from 'utility/error-handler';

import {IUsageSummaryGroup} from 'views/analytics/summary';

/**
* ACTION TYPES
*/

export const USAGE_SUMMARY_FETCHING = 'usage-summary::USAGE_STATISTICS_FETCHING' as const;
export const USAGE_SUMMARY_FETCHING_DONE = 'usage-summary::USAGE_SUMMARY_FETCHING_DONE' as const;
export const USAGE_SUMMARY_FETCHING_ERROR = 'usage-summary::USAGE_SUMMARY_FETCHING_ERROR' as const;

/**
* INTERFACES
*/

interface IRequestUsageSummary {
  type: typeof USAGE_SUMMARY_FETCHING;
}
interface IReceiveUsageSummary {
  type: typeof USAGE_SUMMARY_FETCHING_DONE;
  payload: {data: IUsageSummaryGroup};
}
interface IFailedUsageSummary {
  type: typeof USAGE_SUMMARY_FETCHING_ERROR;
  error: null | IPortalError;
}

export type UsageSummaryActionType = IRequestUsageSummary | IReceiveUsageSummary | IFailedUsageSummary;

interface IUsageSummaryActions {
  request: () => UsageSummaryActionType;
  receive: (payload) => UsageSummaryActionType;
  failed: (error: null | IPortalError) => UsageSummaryActionType;
}

/**
* ACTIONS
*/

const usageSummaryActions: IUsageSummaryActions = {
  request: () => ({type: USAGE_SUMMARY_FETCHING}),
  receive: payload => ({
    type: USAGE_SUMMARY_FETCHING_DONE,
    payload
  }),
  failed: error => ({
    type: USAGE_SUMMARY_FETCHING_ERROR,
    error
  })
};

export const getUsageSummary = (applicationId: string) => async(dispatch: Dispatch<UsageSummaryActionType>): Promise<IUsageSummaryGroup> => {
  const {
    request,
    receive,
    failed
  } = usageSummaryActions;

  dispatch(request());

  try {
    const socket = await websocket.getActiveSocket();
    const last24Hours = getAdjustedTime().subtract(24, 'hours').valueOf();
    const last48Hours = getAdjustedTime().subtract(48, 'hours').valueOf();
    const last30Days = getAdjustedTime().subtract(30, 'days').valueOf();
    const last60Days = getAdjustedTime().subtract(60, 'days').valueOf();
    const currentTime = getAdjustedTime().subtract(1, 'second').valueOf();
    const [usageLast24Hours, usagePreLast24Hours, usageLast30Days, usagePreLast30Days] = await Promise.all([
      fetchUsageStatistics(socket, last24Hours, currentTime, applicationId),
      fetchUsageStatistics(socket, last48Hours, last24Hours, applicationId),
      fetchUsageStatistics(socket, last30Days, currentTime, applicationId),
      fetchUsageStatistics(socket, last60Days, last30Days, applicationId)
    ]);
    const usageSummary = {
      usageLast24Hours: filterUsageByType(usageLast24Hours),
      usagePreLast24Hours: filterUsageByType(usagePreLast24Hours),
      usageLast30Days: filterUsageByType(usageLast30Days),
      usagePreLast30Days: filterUsageByType(usagePreLast30Days)
    };

    dispatch(receive({data: usageSummary}));

    return usageSummary;
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};